import React, { useContext, useEffect, useState } from "react";
import { Formik, Form } from "formik";
import { Spinner } from "reactstrap";
import * as Yup from "yup";
import { Box, Button, Typography } from "@mui/material";
import { Chip, IconButton, LinearProgress } from "@mui/material";
import { toast } from "react-toastify";
import ImageIcon from "@mui/icons-material/Image";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CloseIcon from "@mui/icons-material/Close";
import Autocomplete from "@mui/material/Autocomplete";
import { AutocompleteChangeReason } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import { GenresArray, emailRegex } from "../constants";
import { EmailInviteTextField } from "../../pages/MyTracks/styles";
import { autoCompleteErrorStyle } from "../../pages/MyTracks/styles";
import { autoCompleteStlye, ContainerModal } from "../../pages/MyTracks/styles";
import { styles, useStyles } from "../../pages/MyTracks/styles";
import { UsernameSelectField } from "../../pages/MyTracks/styles";
import { TrackAddModalProps } from "../../pages/MyTracks/types";
import Audio from "../Audio/Audio";
import { awaitableUploadToS3 } from "../../utils/S3Utils";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { LightTooltip } from "../LightToolTip/LightToolTip";
import { Key, SongMetadata, Track, User } from "../../proto/api_pb";
import { InvitedCollaboratorState, FileState } from "../../proto/api_pb";
import { InviteTemplate, InvitedCollaborator } from "../../proto/api_pb";
import { Collaborator, TrackState, TrackType } from "../../proto/api_pb";
import { TrackVersion, KeyNote, KeyScale } from "../../proto/api_pb";
import { AudioMetadata } from "../../proto/api_pb";
import { postTrackVersion } from "../../services/trackService";
import { postTrack, sendInvites } from "../../services/trackService";
import { RpcError } from "grpc-web";
import { AuthContext, AuthContextInterface } from "../../contexts";
import { S3CredentialsContext, UserDetailsContext } from "../../contexts";
import { UserDetailsContextInterface } from "../../contexts";
import { S3CredentialsContextInterface } from "../../contexts";
import KeyComponent from "../TrackComponents/keyComponent/KeyComponent";
import { useNavigate } from "react-router-dom";
import { CustomLinearProgress } from "../ProgressComponents/CustomLinearProgress";
import { determineSetBpm } from "../../utils/bpmUtil";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { isBillingRequired } from "../../utils/subscriptionBillingUtils";
import Mixpanel from "../../utils/mixpanel";
import { trackAddModalStyles } from "./styles";
import { APP_BACKGROUND_COLOR, FONT_FAMILY, TEXT_COLOR } from "../../theme";
const ASSETS_URL = process.env.REACT_APP_ASSET_S3_BUCKET_URL!;

const TRACK_VALIDATION_SCHEMA = Yup.object().shape({
  artist: Yup.string().max(120, "Too Long!").required("the artist is Required"),
  isSongChanged: Yup.boolean(),
  versionLabel: Yup.string().when("isSongChanged", {
    is: true,
    then: Yup.string().required("Must enter version Label"),
  }),
  title: Yup.string()
    .max(120, "Too Long!")
    .required("the song title is Required"),
  bpm: Yup.number()
    .min(0, "Amount must be greater than 0")
    .max(200, "Amount must be less than 200"),
  songUpload: Yup.mixed().required("required"),
  genres: Yup.string().required("Genre is required"),
});

const TrackAddModal: React.FC<TrackAddModalProps> = ({
  open,
  handleClose,
  trackToEdit,
  trackToEditUrl,
}) => {
  const classes = useStyles();
  const { getValidAccessToken } = useGetValidAccessToken();
  const [collaborators, setCollaborators] = useState<Array<Collaborator>>([]);
  const [genres, setGenres] = useState<Array<string>>([]);
  const [collabVal, setCollabVal] = useState("");
  const [usersList, setUsersList] = useState<Array<User>>([]);
  const [previewUrl, setPreviewUrl] = useState("");
  const [versionLabel, setVersionLabel] = useState("");
  const [key, setKey] = useState("");
  const [scale, setScale] = useState("");
  const [showBpmTooltip, setShowBpmTooltip] = useState(false);
  const [showTrackVersionTooltip, setShowTrackVersionTooltip] = useState(false);
  const [showKeyTooltip, setShowKeyTooltip] = useState(false);
  const [showCollabTooltip, setShowCollabTooltip] = useState(false);
  const [trackUrl, setTrackUrl] = useState("");
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { userName, storageUsage, subscriptionTier, allUsers } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);
  const { awsAccessKeyId, awsSecretAccessKey, awsSessionToken } =
    useContext<S3CredentialsContextInterface>(S3CredentialsContext);
  const [songChanged, setSongChanged] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentUpload, setCurrentUpload] = useState("");
  const [bpmLoading, setBpmLoading] = useState(false);
  const navigate = useNavigate();
  const [emailInvite, setEmailInvite] = useState("");
  const [emailInviteList, setEmailInviteList] = useState<string[]>([]);
  const [addedCollabIds, setAddedCollabIds] = useState<string[]>([]);

  const newTrack = {
    artist: trackToEdit ? trackToEdit.getArtist() : "",
    title: trackToEdit ? trackToEdit.getTitle() : "",
    description: trackToEdit ? trackToEdit.getDescription() : "",
    bpm:
      trackToEdit && trackToEdit.getBpm() !== 0
        ? String(trackToEdit.getBpm())
        : "",
    songUpload: undefined,
    coverArtUpload: undefined,
    genres: trackToEdit ? trackToEdit.getSongMetadata()?.getGenre() : "",
    album: trackToEdit ? trackToEdit.getSongMetadata()?.getAlbum() : "",
    key: [],
    versionLabel: "",
  };

  useEffect(() => {
    const isNavigating = isBillingRequired(subscriptionTier, storageUsage);
    if (isNavigating) navigate("/billing");

    if (trackToEdit) {
      const editCall = async () => {
        // initializing collabs
        if (trackToEdit?.getCollaboratorsList())
          setCollaborators([...trackToEdit.getCollaboratorsList()]);

        // initializing key
        const keyDataScale = trackToEdit.getKey()?.getScale();
        if (keyDataScale === KeyScale.KEY_SCALE_MAJOR) setScale("major");
        if (keyDataScale === KeyScale.KEY_SCALE_MINOR) setScale("minor");
        const keyData = trackToEdit.getKey()?.getKey();
        const keyDataNote = trackToEdit.getKey()?.getNote();

        if (keyDataNote === KeyNote.KEY_NOTE_FLAT) setKey(`${keyData}b`);
        if (keyDataNote === KeyNote.KEY_NOTE_SHARP) setKey(`${keyData}#`);
        if (keyDataNote === KeyNote.KEY_NOTE_UNDEFINED) setKey(keyData!);

        // initializing genres
        const songData = trackToEdit.getSongMetadata()?.getGenre();
        setGenres([songData ? songData : ""]);
        setPreviewUrl(trackToEdit.getImageUrl());
        setTrackUrl(trackToEditUrl ?? "");
      };
      editCall();
    }
  }, [
    trackToEdit,
    userId,
    getValidAccessToken,
    trackToEditUrl,
    navigate,
    storageUsage,
    subscriptionTier,
  ]);

  const handleshowBpmTooltip = () => {
    setShowBpmTooltip((prev) => !prev);
  };

  const handleshowKeyTooltip = () => {
    setShowKeyTooltip((prev) => !prev);
  };

  const handleShowCollabTooltip = () => {
    setShowCollabTooltip((prev) => !prev);
  };

  const handleShowVersionTooltip = () => {
    setShowTrackVersionTooltip((prev) => !prev);
  };

  const handleSongChanged = () => {
    setSongChanged(true);
  };

  const handleKey = (
    event: React.MouseEvent<HTMLElement>,
    newKey: string | null
  ) => {
    if (newKey !== null) {
      setKey(newKey);
    } else {
      setKey("");
    }
  };

  const handleScale = (
    event: React.MouseEvent<HTMLElement>,
    newScale: string
  ) => {
    if (newScale !== null) {
      setScale(newScale);
    } else {
      setScale("");
    }
  };

  const handleFile = (file: any) => {
    setPreviewUrl(URL.createObjectURL(file));
    URL.revokeObjectURL(previewUrl);
  };
  const handleDragOver = (event: any) => {
    event.preventDefault();
  };

  let clearAudio: any = null;

  useEffect(() => {
    let userL = allUsers.filter((user) => user.getUsername() !== userName);
    if (trackToEdit) {
      const fetchedCollabs = trackToEdit
        .getCollaboratorsList()
        .map((item) => item.getUsername());
      userL = userL.filter(
        (usr) => !fetchedCollabs.includes(usr.getUsername())
      );
      setCollaborators(trackToEdit.getCollaboratorsList());
    }
    setUsersList(userL);
  }, [allUsers, userName, trackToEdit]);

  const resetFiles = () => {
    setPreviewUrl("");
    clearAudio();
  };

  const saveResetAudioAction = (clearAction: any) => {
    clearAudio = clearAction;
  };

  const handleProgress = (pNum: number, idx: number) => {
    // file + image
    setProgress(pNum / 2 + idx * (100 / 2));
  };

  const onSubmit = async (
    values: any,
    {
      resetForm,
      setSubmitting,
    }: {
      resetForm: any;
      setSubmitting: any;
    }
  ) => {
    try {
      setSubmitting(true);
      let coverArtUrl = previewUrl;
      if (values.coverArtUpload !== undefined) {
        setCurrentUpload("Image");
        const coverArtKey = await awaitableUploadToS3(
          userId,
          values.coverArtUpload,
          `cover-art`,
          values.coverArtUpload!.type,
          awsAccessKeyId,
          awsSecretAccessKey,
          awsSessionToken,
          (progress) => handleProgress(progress, 0)
        );
        coverArtUrl = ASSETS_URL + "/" + coverArtKey;
      }

      let songUrl = trackUrl;
      if (songChanged === true) {
        setCurrentUpload("Track");
        const songKey = await awaitableUploadToS3(
          userId,
          values.songUpload,
          `song`,
          values.songUpload!.type,
          awsAccessKeyId,
          awsSecretAccessKey,
          awsSessionToken,
          (progress) => handleProgress(progress, 1)
        );
        songUrl = ASSETS_URL + "/" + songKey;
      }

      const songMetadata = new SongMetadata();
      songMetadata.setGenre(genres[0]);
      songMetadata.setAlbum(values.album);

      const invitedCollabList: InvitedCollaborator[] = [];
      // handling inviting collabs
      if (emailInviteList.length !== 0) {
        emailInviteList.forEach((email) => {
          const newInvitedCollab = new InvitedCollaborator();
          newInvitedCollab.setEmail(email);
          newInvitedCollab.setInvitedCollaboratorState(
            InvitedCollaboratorState.INVITED_COLLABORATOR_STATE_PENDING
          );
          invitedCollabList.push(newInvitedCollab);
        });
      }

      const newkey = new Key();
      if (key.length !== 0) {
        if (key.length === 1) newkey.setKey(key[0]);
        if (key[1] === "b") newkey.setNote(KeyNote.KEY_NOTE_FLAT);
        if (key[1] === "#") newkey.setNote(KeyNote.KEY_NOTE_SHARP);
        newkey.setKey(key[0]);
      }
      if (scale === "major") newkey.setScale(KeyScale.KEY_SCALE_MAJOR);
      if (scale === "minor") newkey.setScale(KeyScale.KEY_SCALE_MINOR);
      // CALL TO API FOR POSTING TRACK
      if (!trackToEdit) {
        // when a new track is added
        let track = new Track();
        track.setOwnerId(userId);
        track.setArtist(values.artist);
        track.setBpm(values.bpm);
        track.setDescription(values.description);
        track.setImageUrl(coverArtUrl);
        track.setTitle(values.title);
        track.setTrackType(TrackType.TRACK_TYPE_SONG);
        track.setState(TrackState.TRACK_STATE_DRAFT);
        track.setSongMetadata(songMetadata);
        track.setCollaboratorsList(collaborators);
        track.setInvitedCollaboratorsList([...invitedCollabList]);
        track.setKey(newkey);
        let postTrackResponse = await postTrack(
          track,
          await getValidAccessToken()
        );
        if (postTrackResponse instanceof RpcError) {
          //TODO: handle api exception gracefully with error modal, etc
          throw postTrackResponse;
        }
        track = postTrackResponse.getTrack()!;

        const trackVersion = new TrackVersion();
        const audioMetadata = new AudioMetadata();
        audioMetadata.setBpm(values.bpm);
        trackVersion.setLabel(
          versionLabel === "" ? "initial upload" : versionLabel
        );
        trackVersion.setTrackUrl(songUrl);
        trackVersion.setTrackId(track.getId());
        trackVersion.setAudioMetadata(audioMetadata);
        trackVersion.setFileState(FileState.FILE_STATE_ACTIVE);
        const postTrackVersionResponse = await postTrackVersion(
          trackVersion,
          await getValidAccessToken()
        );
        if (postTrackVersionResponse instanceof RpcError) {
          //TODO: handle api exception gracefully with error modal, etc
          throw postTrackVersionResponse;
        }
        track.setTrackVersionId(
          postTrackVersionResponse.getTrackVersion()!.getId()
        );
        postTrackResponse = await postTrack(track, await getValidAccessToken());
        if (postTrackResponse instanceof RpcError) {
          //TODO: handle api exception gracefully with error modal, etc
          throw postTrackResponse;
        }
        if (emailInviteList.length !== 0 || addedCollabIds.length !== 0) {
          try {
            await sendInvites(
              await getValidAccessToken(),
              emailInviteList,
              addedCollabIds,
              userId,
              `${window.location.origin}/track/${postTrackResponse
                ?.getTrack()
                ?.getId()}`,
              values.title,
              InviteTemplate.INVITE_TEMPLATE_COLLABORATE_TRACK
            );
          } catch (error) {
            console.log(error);
          }
        }
        Mixpanel.track("track_add_success", {
          genre: track.getSongMetadata()?.getGenre(),
          collab_count: track.getCollaboratorsList().length,
        });
      } else if (trackToEdit && songChanged === false) {
        // when a track is edited and song is not changed
        const track = trackToEdit;
        track.setOwnerId(trackToEdit.getOwnerId());
        track.setArtist(values.artist);
        track.setBpm(values.bpm);
        track.setDescription(values.description);
        track.setImageUrl(coverArtUrl);
        track.setTitle(values.title);
        track.setTrackType(TrackType.TRACK_TYPE_SONG);
        track.setState(TrackState.TRACK_STATE_DRAFT);
        track.setSongMetadata(songMetadata);
        track.setCollaboratorsList(collaborators);
        track.setInvitedCollaboratorsList([
          ...track.getInvitedCollaboratorsList(),
          ...invitedCollabList,
        ]);
        track.setTrackVersionId(trackToEdit.getTrackVersionId());
        track.setKey(newkey);
        const postTrackResponse = await postTrack(
          track,
          await getValidAccessToken()
        );
        if (postTrackResponse instanceof RpcError) {
          //TODO: handle api exception gracefully with error modal, etc
          throw postTrackResponse;
        }
        if (emailInviteList.length !== 0 || addedCollabIds.length !== 0) {
          try {
            await sendInvites(
              await getValidAccessToken(),
              emailInviteList,
              addedCollabIds,
              userId,
              `${window.location.origin}/track/${postTrackResponse
                ?.getTrack()
                ?.getId()}`,
              values.title,
              InviteTemplate.INVITE_TEMPLATE_COLLABORATE_TRACK
            );
          } catch (error) {
            console.log(error);
          }
        }
      } else {
        // when a track is edited and song is changed (needs a new version)
        const track = trackToEdit;
        track.setOwnerId(trackToEdit.getOwnerId());
        track.setArtist(values.artist);
        track.setBpm(values.bpm);
        track.setDescription(values.description);
        track.setImageUrl(coverArtUrl);
        track.setTitle(values.title);
        track.setTrackType(TrackType.TRACK_TYPE_SONG);
        track.setState(TrackState.TRACK_STATE_DRAFT);
        track.setSongMetadata(songMetadata);
        track.setCollaboratorsList(collaborators);
        track.setInvitedCollaboratorsList([
          ...track.getInvitedCollaboratorsList(),
          ...invitedCollabList,
        ]);
        track.setKey(newkey);

        const trackVersion = new TrackVersion();
        const audioMetadata = new AudioMetadata();
        audioMetadata.setBpm(values.bpm);
        trackVersion.setLabel(
          versionLabel === "" ? "updated version" : versionLabel
        );
        trackVersion.setAudioMetadata(audioMetadata);
        trackVersion.setTrackUrl(songUrl);
        trackVersion.setTrackId(track.getId());
        trackVersion.setFileState(FileState.FILE_STATE_ACTIVE);
        const postTrackVersionResponse = await postTrackVersion(
          trackVersion,
          await getValidAccessToken()
        );
        if (postTrackVersionResponse instanceof RpcError) {
          //TODO: handle api exception gracefully with error modal, etc
          throw postTrackVersionResponse;
        }
        track.setTrackVersionId(
          postTrackVersionResponse.getTrackVersion()!.getId()
        );
        const postTrackResponse = await postTrack(
          track,
          await getValidAccessToken()
        );
        if (postTrackResponse instanceof RpcError) {
          //TODO: handle api exception gracefully with error modal, etc
          throw postTrackResponse;
        }
        if (emailInviteList.length !== 0 || addedCollabIds.length !== 0) {
          try {
            await sendInvites(
              await getValidAccessToken(),
              emailInviteList,
              addedCollabIds,
              userId,
              `${window.location.origin}/track/${postTrackResponse
                ?.getTrack()
                ?.getId()}`,
              values.title,
              InviteTemplate.INVITE_TEMPLATE_COLLABORATE_TRACK
            );
          } catch (error) {
            console.log(error);
          }
        }
        Mixpanel.track("track_edit_success", {
          genre: track.getSongMetadata()?.getGenre(),
          collab_count: track.getCollaboratorsList().length,
        });
      }

      // once api calls are done
      setSubmitting(false);
      setSongChanged(false);
      resetForm();
      resetFiles();
      //clearing genres input field
      setGenres([]);
      setScale("");
      setKey("");
      setCollaborators([]);
      setEmailInviteList([]);
      trackToEdit
        ? handleClose(true, "Track successfully updated!")
        : handleClose(true, "Track successfully added!");
    } catch (error) {
      console.log(error);
      alert(error);
      setSongChanged(false);
      setSubmitting(false);
    }
  };

  const addCollaborator = (newCollaborator: string) => {
    if (newCollaborator) {
      if (
        collaborators.find(
          (collaborator) => collaborator.getUsername() === newCollaborator
        ) === undefined &&
        usersList.some((user) => user.getUsername() === newCollaborator)
      ) {
        const addedCollab = usersList.find(
          (user) => user.getUsername() === newCollaborator
        );

        if (addedCollab) {
          const newCollab = new Collaborator();
          newCollab.setUserId(addedCollab?.getUserId());
          newCollab.setUsername(addedCollab?.getUsername());
          const updatedCollaborators = [...collaborators, newCollab];

          setAddedCollabIds([...addedCollabIds, addedCollab.getUserId()]);
          setCollaborators(updatedCollaborators);
        }

        const newUserList = usersList.filter(
          (user) => user.getUsername() !== newCollaborator
        );
        setUsersList([...newUserList]);
        setCollabVal("");
      } else {
        toast.info("Username does not exist");
      }
    }
  };

  const removeCollaborator = (collaborator: Collaborator) => {
    const updatedCollaborators = collaborators.filter(
      (item) => item !== collaborator
    );
    setCollaborators(updatedCollaborators);

    // updating userIds list that is being passed in sendInvite
    const updatedUserIdList = addedCollabIds.filter(
      (item) => item !== collaborator.getUserId()
    );
    setAddedCollabIds([...updatedUserIdList]);

    const newUser = new User();
    newUser.setUsername(collaborator.getUsername());
    newUser.setUserId(collaborator.getUserId());

    const newUsersList = [...usersList, newUser];
    setUsersList([...newUsersList]);
  };

  const handleCollabInvites = (event: React.SyntheticEvent<Element, Event>) => {
    if (emailInvite) {
      if (emailInvite.match(emailRegex)) {
        setEmailInviteList([...emailInviteList, emailInvite]);
        setEmailInvite("");
      } else {
        toast.info("Invalid email address!");
      }
    }
  };

  const handleRemoveEmail = (email: string) => {
    const updateEmailList = emailInviteList.filter((item) => item !== email);
    setEmailInviteList(updateEmailList);
  };

  return (
    <ContainerModal
      open={open}
      onClose={() => handleClose(false)}
      style={{ overflow: "auto" }}
    >
      <Box
        style={{ backgroundColor: APP_BACKGROUND_COLOR, color: TEXT_COLOR }}
        className="form-container"
      >
        <Box>
          <Box style={styles.modalHeaderContainer}>
            <span style={styles.modalHeader}>
              {" "}
              {trackToEdit ? "Edit Track" : "Add Track"}
            </span>
            <IconButton onClick={() => handleClose(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <hr style={{ marginBottom: 30 }} />
          <Formik
            initialValues={newTrack}
            onSubmit={onSubmit}
            validationSchema={TRACK_VALIDATION_SCHEMA}
          >
            {({
              values,
              handleChange,
              isSubmitting,
              setFieldValue,
              handleBlur,
              errors,
              touched,
            }) => (
              <Form>
                <Box style={{ color: TEXT_COLOR }} className="form-content">
                  <label
                    style={{
                      color:
                        errors.songUpload && touched.songUpload
                          ? "red"
                          : TEXT_COLOR,
                    }}
                    htmlFor="audio"
                    className="uploadLabel"
                  >
                    Upload Audio
                    <span className="asterisk">*</span>
                  </label>
                  <Audio
                    handleBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    touched={touched}
                    errors={errors}
                    isSubmitting={isSubmitting}
                    transferClearAudio={saveResetAudioAction}
                    songTitle={trackToEdit?.getTitle()}
                    songUrl={trackUrl}
                    handleSongChanged={handleSongChanged}
                    determineBpm={determineSetBpm}
                    setBpmLoading={setBpmLoading}
                  />
                  <p className="input-description">
                    File type supported: MP3, WAV, OGG. Max size: 100 MB
                  </p>
                  {songChanged && (
                    <Box className="input">
                      <label
                        style={{
                          color:
                            errors.versionLabel && touched.versionLabel
                              ? "red"
                              : TEXT_COLOR,
                        }}
                        htmlFor="label"
                      >
                        Version Label<span className="asterisk">*</span>
                        <Box
                          style={{ display: "inline" }}
                          onMouseOver={() => setShowTrackVersionTooltip(true)}
                          onMouseLeave={() => setShowTrackVersionTooltip(false)}
                        >
                          <LightTooltip
                            title="Version label uniquely identifies this uploaded version of your track"
                            placement="top"
                            open={showTrackVersionTooltip}
                            onClick={handleShowVersionTooltip}
                          >
                            <IconButton disableRipple>
                              <HelpOutlineIcon
                                color="info"
                                style={{ height: 16, width: 16 }}
                              />
                            </IconButton>
                          </LightTooltip>
                        </Box>
                      </label>
                      <input
                        style={{
                          color: TEXT_COLOR,
                          backgroundColor: 'inherit',
                          borderColor:
                            errors.versionLabel && touched.versionLabel
                              ? "red"
                              : TEXT_COLOR,
                        }}
                        type="text"
                        name="label"
                        id="label"
                        placeholder="Add a label for this version of track"
                        value={versionLabel}
                        onChange={(event) => {
                          setFieldValue("versionLabel", event.target.value);
                          setVersionLabel(event.target.value);
                        }}
                        disabled={isSubmitting}
                      />
                    </Box>
                  )}
                  <label htmlFor="artwork" className="uploadLabel">
                    Upload Artwork{" "}
                    <span className="optional-input">(optional)</span>
                  </label>
                  <label
                    htmlFor="cover-art-upload"
                    className="image-upload cover-upload"
                    style={{
                      borderStyle: previewUrl ? "none" : "dashed",
                      borderWidth: "2px",
                    }}
                    onBlur={handleBlur}
                    onDragOver={handleDragOver}
                    onDrop={(event: any) => {
                      //prevent the browser from opening the image
                      event.preventDefault();
                      event.stopPropagation();
                      if (
                        event.dataTransfer.files[0] &&
                        event.dataTransfer.files[0]?.type?.split("/")[0] !==
                          "image"
                      ) {
                        toast.info("Upload an image file");
                      } else {
                        setFieldValue(
                          "coverArtUpload",
                          event.dataTransfer.files[0]
                        );
                        const imageFile = event.dataTransfer.files[0];
                        handleFile(imageFile);
                      }
                    }}
                    onClick={handleBlur}
                  >
                    <input
                      style={{ color: TEXT_COLOR, backgroundColor: TEXT_COLOR }}
                      type="file"
                      name="coverArtUpload"
                      id="cover-art-upload"
                      onChange={(event: any) => {
                        handleFile(event.target.files[0]);
                        setFieldValue(
                          "coverArtUpload",
                          event.currentTarget.files?.[0]
                        );
                        event.currentTarget.value = "";
                      }}
                      accept="image/*"
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                    />
                    {!previewUrl && (
                      <>
                        <ImageIcon
                          sx={{
                            ...styles.modalImagePlaceholder,
                            ...{ color: TEXT_COLOR },
                          }}
                        />
                        <img
                          style={{ color: TEXT_COLOR }}
                          alt=""
                          className="edit"
                        />
                      </>
                    )}

                    {previewUrl && (
                      <img
                        src={previewUrl}
                        alt="cover"
                        height={"100%"}
                        width={"100%"}
                        color="white"
                      />
                    )}
                  </label>
                  <p className="input-description">
                    Image type supported: JPG or JPEG. Max size 10 MB
                  </p>
                  <p className="input-description" style={{ marginTop: -25 }}>
                    Square at least 3000x3000 pixels, not blurry or pixelated.
                  </p>
                  <Box className="input">
                    <label
                      style={{
                        color:
                          errors.artist && touched.artist ? "red" : TEXT_COLOR,
                      }}
                      htmlFor="artist"
                    >
                      Artist
                      <span className="asterisk">*</span>
                    </label>
                    <input
                      style={{
                        backgroundColor: APP_BACKGROUND_COLOR,
                        color: TEXT_COLOR,
                        borderColor:
                          errors.artist && touched.artist ? "red" : TEXT_COLOR,
                      }}
                      type="text"
                      name="artist"
                      id="artist"
                      placeholder="Artist"
                      value={values.artist}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                    />
                  </Box>

                  <Box className="input">
                    <label
                      style={{
                        color:
                          errors.title && touched.title ? "red" : TEXT_COLOR,
                      }}
                      htmlFor="title"
                    >
                      Title
                      <span className="asterisk">*</span>
                    </label>
                    <input
                      style={{
                        backgroundColor: APP_BACKGROUND_COLOR,
                        color: TEXT_COLOR,
                        borderColor:
                          errors.title && touched.title ? "red" : TEXT_COLOR,
                      }}
                      type="text"
                      name="title"
                      id="title"
                      placeholder="Song Title"
                      value={values.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                    />
                  </Box>
                  <Box className="input">
                    <label style={{ color: TEXT_COLOR }} htmlFor="album">
                      Album <span className="optional-input">(optional)</span>
                    </label>
                    <input
                      style={trackAddModalStyles.albumInput}
                      type="text"
                      name="album"
                      id="album"
                      placeholder="Album"
                      value={values.album}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                    />
                  </Box>
                  <Box className="input">
                    <label style={{ color: TEXT_COLOR }} htmlFor="description">
                      Description{" "}
                      <span className="optional-input">(optional)</span>
                    </label>
                    <textarea
                      style={{
                        ...styles.modalDescription,
                        ...{
                          color: TEXT_COLOR,
                          border: `1px solid ${TEXT_COLOR}`,
                        },
                      }}
                      name="description"
                      id="description"
                      placeholder="Track Description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      cols={30}
                      rows={4}
                      disabled={isSubmitting}
                    ></textarea>
                  </Box>
                  <Box>
                    <Box style={{ marginBottom: 20 }}>
                      <label
                        htmlFor="bio"
                        style={{
                          ...trackAddModalStyles.genreLabel,
                          ...{
                            color:
                              errors.genres && touched.genres
                                ? "red"
                                : TEXT_COLOR,
                          },
                        }}
                      >
                        {" "}
                        Genre
                        <span className="asterisk">*</span>
                      </label>
                      <Stack spacing={3} sx={{}}>
                        <Autocomplete
                          disabled={isSubmitting}
                          autoComplete
                          value={genres[0] || null}
                          sx={
                            errors.genres && touched.genres
                              ? autoCompleteErrorStyle
                              : autoCompleteStlye
                          }
                          options={GenresArray}
                          onChange={(
                            event: React.SyntheticEvent<Element, Event>,
                            value: string | null,
                            _reason: AutocompleteChangeReason
                          ) => {
                            if (value != null) {
                              setGenres([value]);
                              setFieldValue("genres", value);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label=" "
                              InputLabelProps={{
                                shrink: false,
                              }}
                              classes={{ root: classes.customTextField }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props} style={{ fontFamily: FONT_FAMILY }}>
                              {option}
                            </li>
                          )}
                        />
                      </Stack>
                    </Box>
                  </Box>
                  {
                    <Box className="input">
                      <label
                        htmlFor="bpm"
                        style={{
                          ...styles.modalBpmLabel,
                          ...{ color: TEXT_COLOR },
                        }}
                      >
                        {" "}
                        BPM <span className="optional-input">(optional)</span>
                        <Box
                          style={{ display: "inline" }}
                          onMouseOver={() => setShowBpmTooltip(true)}
                          onMouseLeave={() => setShowBpmTooltip(false)}
                        >
                          <LightTooltip
                            title="Beats per minute (will be detected automatically)"
                            placement="top"
                            open={showBpmTooltip}
                            onClick={handleshowBpmTooltip}
                          >
                            <IconButton disableRipple>
                              <HelpOutlineIcon
                                color="info"
                                style={{ height: 16, width: 16 }}
                              />
                            </IconButton>
                          </LightTooltip>
                        </Box>
                      </label>
                      {!bpmLoading && (
                        <Box style={{ display: "flex", flexDirection: "row" }}>
                          <input
                            style={{
                              backgroundColor: APP_BACKGROUND_COLOR,
                              color: "white",
                              ...(!songChanged
                                ? { opacity: 0.6, cursor: "not-allowed" }
                                : {}),
                            }}
                            type="number"
                            name="bpm"
                            id="bpm"
                            min="0"
                            max="200"
                            step="1"
                            placeholder="BPM range 0-200"
                            value={values.bpm}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isSubmitting || !songChanged}
                          />
                        </Box>
                      )}
                      {bpmLoading && (
                        <Box sx={trackAddModalStyles.bpmLoadingContainer}>
                          <LinearProgress color="inherit" />
                        </Box>
                      )}
                    </Box>
                  }
                  <KeyComponent
                    songKey={key}
                    scale={scale}
                    showKeyTooltip={showKeyTooltip}
                    handleKey={handleKey}
                    handleScale={handleScale}
                    handleshowKeyTooltip={handleshowKeyTooltip}
                    setShowKeyTooltip={setShowKeyTooltip}
                  />
                  <Box className="collab-label">
                    <span>Add Collaborators </span>
                    <span className="optional-input">(optional)</span>
                    <Box
                      style={{ display: "inline" }}
                      onMouseOver={() => setShowCollabTooltip(true)}
                      onMouseLeave={() => setShowCollabTooltip(false)}
                    >
                      <LightTooltip
                        title="You can add artists, composers, and more collaborators"
                        placement="top"
                        open={showCollabTooltip}
                        onClick={handleShowCollabTooltip}
                      >
                        <IconButton disableRipple>
                          <HelpOutlineIcon
                            color="info"
                            style={{ height: 16, width: 16 }}
                          />
                        </IconButton>
                      </LightTooltip>
                    </Box>
                  </Box>
                  <hr />
                  <Box sx={trackAddModalStyles.userNameSelectFieldContainer}>
                    <Box sx={{ width: "80%" }}>
                      <label
                        htmlFor="newCollaboratorUser"
                        style={styles.modalCreatorUsername}
                      >
                        {"Creator's Username"}
                      </label>
                      <UsernameSelectField
                        freeSolo
                        forcePopupIcon={false}
                        isOptionEqualToValue={(option, value) =>
                          option === value
                        }
                        inputValue={collabVal}
                        onInputChange={(
                          event: React.SyntheticEvent<Element, Event>,
                          value: string
                        ) => {
                          setCollabVal(value);
                        }}
                        options={usersList
                          .map((item) => item.getUsername())
                          .sort()}
                        renderInput={(params) => (
                          <TextField
                            style={{
                              backgroundColor: APP_BACKGROUND_COLOR,
                              color: TEXT_COLOR,
                            }}
                            {...params}
                            label=" "
                            InputLabelProps={{
                              shrink: false,
                            }}
                            classes={{ root: classes.customTextField }}
                          />
                        )}
                        renderOption={(props, option: any) => (
                          <li {...props} style={{ fontFamily: FONT_FAMILY }}>
                            {option}
                          </li>
                        )}
                      />
                    </Box>
                    <Button
                      disabled={collabVal === ""}
                      sx={trackAddModalStyles.addUserNameButton}
                      size="small"
                      variant="contained"
                      onClick={() => {
                        if (!isSubmitting) {
                          addCollaborator(collabVal);
                        }
                      }}
                    >
                      Add
                    </Button>
                  </Box>
                  <Box
                    className="collaborators"
                    style={{ margin: "20px 0 40px" }}
                  >
                    <Box className="collaborators-target">
                      {collaborators &&
                        collaborators.length > 0 &&
                        collaborators.map((collaborator, index) => (
                          <Box className="collaborator" key={index}>
                            <p
                              style={{
                                fontFamily: FONT_FAMILY,
                                flexGrow: 1,
                              }}
                            >
                              {collaborator.getUsername()}
                            </p>
                            {!isSubmitting &&
                              collaborator.getUserId() !== userId &&
                              collaborator.getUserId() !==
                                trackToEdit?.getOwnerId() && (
                                <IconButton
                                  onClick={() =>
                                    removeCollaborator(collaborator)
                                  }
                                >
                                  <DeleteForeverIcon />
                                </IconButton>
                              )}
                            {collaborator.getUserId() ===
                              trackToEdit?.getOwnerId() && (
                              <Chip
                                label="Owner"
                                color="primary"
                                sx={{
                                  marginRight:
                                    trackToEdit?.getOwnerId() === userId
                                      ? 1
                                      : 0,
                                }}
                              />
                            )}
                            {collaborator.getUserId() === userId && (
                              <Chip label="You" color="info" />
                            )}
                          </Box>
                        ))}
                    </Box>
                  </Box>

                  <div style={trackAddModalStyles.inviteEmailMainContainer} />
                  <Box sx={trackAddModalStyles.inviteEmailContainer}>
                    <Box sx={{ width: "80%" }}>
                      <label
                        htmlFor="emailInvite"
                        style={styles.modalCreatorUsername}
                      >
                        {"Invite Collaborator"}
                      </label>
                      <EmailInviteTextField
                        hiddenLabel
                        variant="outlined"
                        classes={{ root: classes.customTextField }}
                        placeholder="Send an invite email to a collaborator"
                        value={emailInvite}
                        onChange={(
                          event: React.ChangeEvent<
                            HTMLTextAreaElement | HTMLInputElement
                          >
                        ) => setEmailInvite(event.target.value)}
                      />
                    </Box>
                    <Button
                      disabled={emailInvite === ""}
                      sx={trackAddModalStyles.inviteEmailButton}
                      size="small"
                      variant="contained"
                      onClick={handleCollabInvites}
                    >
                      Add
                    </Button>
                  </Box>

                  <Box style={{ marginBottom: 10, width: "100%" }}>
                    <Box>
                      {emailInviteList &&
                        emailInviteList.length > 0 &&
                        emailInviteList.map((email, index) => (
                          <Box
                            className="collaborator"
                            style={{ width: "100%" }}
                            key={index}
                          >
                            <p style={trackAddModalStyles.email}>{email}</p>

                            <IconButton
                              onClick={() => handleRemoveEmail(email)}
                            >
                              <DeleteForeverIcon />
                            </IconButton>
                          </Box>
                        ))}
                    </Box>
                    {/* if there are invited collabs with pending invitations */}

                    <Box>
                      {trackToEdit &&
                        trackToEdit.getInvitedCollaboratorsList().length > 0 &&
                        trackToEdit
                          .getInvitedCollaboratorsList()
                          .map((item, index) => {
                            if (
                              item.getInvitedCollaboratorState() ===
                              InvitedCollaboratorState.INVITED_COLLABORATOR_STATE_PENDING
                            )
                              return (
                                <Box
                                  className="collaborator"
                                  style={{ width: "100%" }}
                                  key={index}
                                >
                                  <p style={trackAddModalStyles.email}>
                                    {item.getEmail()}
                                  </p>
                                  <Chip label="Sent" color="primary" />
                                </Box>
                              );
                          })}
                    </Box>
                  </Box>

                  <hr />
                  <Button
                    disabled={isSubmitting}
                    fullWidth
                    type="submit"
                    sx={styles.modalSubmitButton}
                    size="small"
                    variant="contained"
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="grow"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          color="black"
                        />{" "}
                        <span style={trackAddModalStyles.uploadingSpan}>
                          {`Uploading ${currentUpload}`}
                        </span>
                      </>
                    ) : (
                      <Typography
                        variant="h1"
                        sx={styles.modalSubmitButtonText}
                      >
                        {trackToEdit ? "Update Track" : "Add Track"}
                      </Typography>
                    )}
                  </Button>
                  {isSubmitting && <CustomLinearProgress value={progress} />}
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Box>
    </ContainerModal>
  );
};

export default TrackAddModal;
